import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { PropagateLoader } from 'react-spinners';
import * as mime from 'mime';

import { FaLink } from 'react-icons/fa';

import InfoInput from '~/components/Inputs/Info';
import TextArea from '~/components/TextArea';
import AudioIcon from '~/components/Icons/Files/Audio';
import PdfIcon from '~/components/Icons/Files/Pdf';
import PresentationIcon from '~/components/Icons/Files/Presentation';
import SpreadsheetIcon from '~/components/Icons/Files/Spreadsheet';
import TextIcon from '~/components/Icons/Files/Text';
import VideoIcon from '~/components/Icons/Files/Video';
import WordIcon from '~/components/Icons/Files/Word';
import Paginator from '~/components/Paginator';
import PrevButton from '~/components/Paginator/PrevButton';
import NextButton from '~/components/Paginator/NextButton';
import CenterButton from '~/components/Paginator/CenterButton';
import CloseButton from '~/components/Buttons/Close';

import api from '~/services/api';

import {
  StyledModal,
  MainContainer,
  Title,
  Container,
  Content,
  ContentSplit,
  Wrapper,
  HalfWrapper,
  FilesWrapper,
  FilterContent,
  Table,
  Th,
  Td,
  FileWrapper,
  FileImageWrapper,
  FileImage,
  UrlIcon,
  ButtonsContent,
  ButtonContainer,
} from './styles';

interface IDisplayDetailProps {
  isOpen: boolean;
  onClose: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  store_relationship_code: string;
  store_relationship_display_id: string;
  completion_date: Date;
  type: string;
  record_type: string;
  note: string;
  created_at: Date;
  updated_at: Date;
  user_inclusion: string;
  user_update: string;
}

interface IStoreRelationshipDisplayFile {
  id: string;
  name: string;
  size: number;
  key: string;
  url: string;
  file_type_description: string;
  store_relationship_display_id: string;
  user_id: string;
  store_relationship_display_file_received_id: string;
  user: {
    id: string;
    name: string;
    type: string;
  };
  created_at: string;
}

const DisplayDetail: React.FC<IDisplayDetailProps> = ({
  isOpen,
  onClose,
  setIsOpen,
  store_relationship_code,
  store_relationship_display_id,
  completion_date,
  type,
  record_type,
  note,
  created_at,
  updated_at,
  user_inclusion,
  user_update,
}: IDisplayDetailProps) => {
  const [page, setPage] = useState(0);
  const [pageFilter, setPageFilter] = useState(0);
  const [filter, setFilter] = useState('');
  const [filterHelper, setFilterHelper] = useState('');
  const [refresh, setRefresh] = useState(true);
  const [storeRelationshipDisplayFiles, setStoreRelationshipDisplayFiles] =
    useState<IStoreRelationshipDisplayFile[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState(true);

  const handleClose = (): void => {
    setIsOpen(false);
    onClose();
    setPage(0);
    setPageFilter(0);
    setFilter('');
    setFilterHelper('');
    setRefresh(true);
    setStoreRelationshipDisplayFiles([]);
    setLoading(true);
    setNextDisabled(true);
  };

  useEffect(() => {
    if (refresh && store_relationship_display_id) {
      setRefresh(false);
      let pageSearch = 0;
      let foundData = false;

      if (filter !== filterHelper) {
        setPage(0);
        setPageFilter(0);
      }

      setFilterHelper(filter);

      if (filter !== '') {
        pageSearch = pageFilter;
      } else {
        pageSearch = page;
      }

      api
        .get(
          `/storerelationshipdisplayfiles/storerelationshipdisplay` +
            `?store_relationship_display_id=${store_relationship_display_id}` +
            `&page=${pageSearch}` +
            `&filter=${filter}`,
        )
        .then(firstResponse => {
          if (firstResponse.data.length > 0) {
            foundData = true;
            setStoreRelationshipDisplayFiles(firstResponse.data);
            setLoading(false);
          }

          if (!foundData) {
            if (pageSearch - 1 >= 0) {
              pageSearch -= 1;
              if (filter !== '') {
                setPageFilter(pageFilter - 1);
              } else {
                setPage(page - 1);
              }
            }

            api
              .get(
                `/storerelationshipdisplayfiles/storerelationshipdisplay` +
                  `?store_relationship_display_id=${store_relationship_display_id}` +
                  `&page=${pageSearch}` +
                  `&filter=${filter}`,
              )
              .then(secondResponse => {
                setStoreRelationshipDisplayFiles(secondResponse.data);
                setLoading(false);

                api
                  .get(
                    `/storerelationshipdisplayfiles/storerelationshipdisplay` +
                      `?store_relationship_display_id=${store_relationship_display_id}` +
                      `&page=${pageSearch + 1}` +
                      `&filter=${filter}`,
                  )
                  .then(thirdResponse => {
                    setNextDisabled(false);
                    if (thirdResponse.data.length === 0) {
                      setNextDisabled(true);
                    }
                  });
              });
          } else {
            api
              .get(
                `/storerelationshipdisplayfiles/storerelationshipdisplay` +
                  `?store_relationship_display_id=${store_relationship_display_id}` +
                  `&page=${pageSearch + 1}` +
                  `&filter=${filter}`,
              )
              .then(fourthResponse => {
                setNextDisabled(false);
                if (fourthResponse.data.length === 0) {
                  setNextDisabled(true);
                }
              });
          }
        });
    }
  }, [
    page,
    pageFilter,
    filter,
    filterHelper,
    refresh,
    store_relationship_display_id,
  ]);

  function handlePage(action_type: string): void {
    if (action_type === 'backward') {
      if (filter !== '') {
        setPageFilter(pageFilter - 1);
      } else {
        setPage(page - 1);
      }
    } else if (action_type === 'forward') {
      if (filter !== '') {
        setPageFilter(pageFilter + 1);
      } else {
        setPage(page + 1);
      }
    } else if (filter !== '') {
      setPageFilter(0);
    } else {
      setPage(0);
    }
    setRefresh(true);
  }

  function handlePrevButtonDisabled(): boolean {
    let validationPage = 0;

    if (filter !== '') {
      validationPage = pageFilter;
    } else {
      validationPage = page;
    }

    if (validationPage === 0) {
      return true;
    }

    return false;
  }

  function handleFilterChange(value: string): void {
    setFilter(value);
    setRefresh(true);
  }

  function handleDisplayFileImageIcon(displayFileUrl: string): React.ReactNode {
    const displayFileMimeType = mime.getType(displayFileUrl);

    if (
      displayFileMimeType === 'audio/mp3' ||
      displayFileMimeType === 'audio/mpeg'
    ) {
      return <AudioIcon iconSize={50} />;
    }

    if (displayFileMimeType === 'application/pdf') {
      return <PdfIcon iconSize={50} />;
    }

    if (
      displayFileMimeType ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      displayFileMimeType === 'application/vnd.oasis.opendocument.presentation'
    ) {
      return <PresentationIcon iconSize={50} />;
    }

    if (
      displayFileMimeType ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      displayFileMimeType === 'application/vnd.oasis.opendocument.spreadsheet'
    ) {
      return <SpreadsheetIcon iconSize={50} />;
    }

    if (displayFileMimeType === 'text/plain') {
      return <TextIcon iconSize={50} />;
    }

    if (
      displayFileMimeType === 'image/gif' ||
      displayFileMimeType === 'video/quicktime' ||
      displayFileMimeType === 'video/mp4'
    ) {
      return <VideoIcon iconSize={40} />;
    }

    if (
      displayFileMimeType ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      displayFileMimeType === 'application/vnd.oasis.opendocument.text'
    ) {
      return <WordIcon iconSize={50} />;
    }

    return (
      <FileImageWrapper>
        <FileImage src={displayFileUrl} />
      </FileImageWrapper>
    );
  }

  function handleTable(): React.ReactNode {
    if (storeRelationshipDisplayFiles) {
      if (storeRelationshipDisplayFiles.length > 0) {
        return (
          <Table>
            <thead>
              <tr>
                <Th width="20%" textAlign="left" />
                <Th width="20%" textAlign="left">
                  Data
                </Th>
                <Th width="30%" textAlign="left">
                  Nome
                </Th>
                <Th width="20%" textAlign="left">
                  Tipo
                </Th>
                <Th width="10%" textAlign="center" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <Td colSpan={4} padding="1rem" textAlign="center">
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              )}
              {storeRelationshipDisplayFiles?.map(
                storeRelationshipDisplayFile => (
                  <tr key={storeRelationshipDisplayFile.id}>
                    <Td width="20%" textAlign="left" heading={' '}>
                      <FileWrapper>
                        {handleDisplayFileImageIcon(
                          storeRelationshipDisplayFile.url,
                        )}
                      </FileWrapper>
                    </Td>
                    <Td width="20%" textAlign="left" heading="DATA">
                      {new Date(
                        storeRelationshipDisplayFile.created_at,
                      ).toLocaleDateString()}
                    </Td>
                    <Td width="30%" textAlign="left" heading="NOME">
                      {storeRelationshipDisplayFile.name}
                    </Td>
                    <Td width="20%" textAlign="left" heading="TIPO">
                      {storeRelationshipDisplayFile.file_type_description}
                    </Td>
                    <Td
                      width="10%"
                      textAlign="center"
                      display="flex"
                      heading={' '}
                    >
                      <UrlIcon>
                        <a
                          href={storeRelationshipDisplayFile.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaLink size={22} />
                        </a>
                      </UrlIcon>
                    </Td>
                  </tr>
                ),
              )}
            </tbody>
          </Table>
        );
      }
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th width="100%" textAlign="center" />
          </tr>
        </thead>
        <tbody>
          <tr>
            <Td width="100%" textAlign="center">
              Nenhuma informação disponível
            </Td>
          </tr>
        </tbody>
      </Table>
    );
  }

  return (
    <StyledModal
      isOpen={isOpen}
      style={{
        overlay: {
          zIndex: 999,
          overflowY: 'auto',
          position: 'fixed',
          background: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <MainContainer>
        <Title>Detalhes Display</Title>
        <Container>
          <ContentSplit maxWidth="680px">
            <HalfWrapper marginRight="1rem">
              <InfoInput
                name="store_relationship_code"
                labelFor="store_relationship_code"
                labelText="Cód. Registro"
                type="text"
                defaultValue={store_relationship_code}
                disabled
              />
            </HalfWrapper>

            <HalfWrapper>
              <InfoInput
                name="completion_date"
                labelFor="completion_date"
                labelText="Data Conclusão"
                type="text"
                defaultValue={new Date(completion_date).toLocaleDateString()}
                disabled
              />
            </HalfWrapper>
          </ContentSplit>

          <Content maxWidth="680px">
            <Wrapper>
              <InfoInput
                name="type"
                labelFor="type"
                labelText="Tipo"
                type="text"
                defaultValue={type}
                disabled
              />
            </Wrapper>
          </Content>

          <Content maxWidth="680px">
            <Wrapper>
              <InfoInput
                name="record_type"
                labelFor="record_type"
                labelText="Tipo de Registro"
                type="text"
                defaultValue={record_type}
                disabled
              />
            </Wrapper>
          </Content>

          <Content maxWidth="680px">
            <Wrapper>
              <TextArea
                name="note"
                labelFor="note"
                labelText="Observação"
                customHeight="5rem"
                defaultValue={note}
                maxLength={250}
                disabled
              />
            </Wrapper>
          </Content>

          <FilesWrapper>
            <FilterContent>
              <InfoInput
                name="search"
                labelText="Pesquisar"
                type="text"
                onChange={event => handleFilterChange(event.target.value)}
              />
            </FilterContent>

            {handleTable()}

            <ButtonsContent>
              <Paginator>
                <PrevButton
                  disabled={handlePrevButtonDisabled()}
                  onClick={() => handlePage('backward')}
                />
                <CenterButton onClick={() => handlePage('reset')} />
                <NextButton
                  disabled={nextDisabled}
                  onClick={() => handlePage('forward')}
                />
              </Paginator>
            </ButtonsContent>
          </FilesWrapper>

          <ContentSplit maxWidth="680px">
            <HalfWrapper marginRight="1rem">
              <InfoInput
                name="created_at"
                labelFor="created_at"
                labelText="Data Inclusão"
                type="text"
                defaultValue={new Date(created_at).toLocaleDateString()}
                disabled
              />
            </HalfWrapper>

            <HalfWrapper>
              <InfoInput
                name="updated_at"
                labelFor="updated_at"
                labelText="Data Atualização"
                type="text"
                defaultValue={new Date(updated_at).toLocaleDateString()}
                disabled
              />
            </HalfWrapper>
          </ContentSplit>

          <ContentSplit maxWidth="680px">
            <HalfWrapper marginRight="1rem">
              <InfoInput
                name="user_inclusion"
                labelFor="user_inclusion"
                labelText="Usuário Inclusão"
                type="text"
                defaultValue={user_inclusion}
                disabled
              />
            </HalfWrapper>

            <HalfWrapper>
              <InfoInput
                name="user_update"
                labelFor="user_update"
                labelText="Usuário Atualização"
                type="text"
                defaultValue={user_update}
                disabled
              />
            </HalfWrapper>
          </ContentSplit>

          <ButtonContainer>
            <CloseButton onClick={() => handleClose()} />
          </ButtonContainer>
        </Container>
      </MainContainer>
    </StyledModal>
  );
};

export default DisplayDetail;
